import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils } from "@/comn/utils";
import { Button, Grid, Group, IconButton, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import {
    APIS,
    URLS,
    SF_APRV_PRCSS_PATH_MGMT_INFO,
    SG_APRV_PATH_SRCH_LIST,
    SG_APRV_PATH_SRCH_ADD_LIST,
} from "./services/OsciAprvPathService";

/*
 * registration for Approval Path Management
 * !== 결재선 관리 등록 ==!
 */
export const OSCI0108002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const [aprvPathSrchList, setAprvPathSrchList] = useState(comnUtils.getGridData([]));

    useResource({
        defaultSchema: [{ area: "cstmCd" }, { area: "cstmOfcrCd" }, { area: "comnCd", comnCd: "OSC0089" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Approval Path Information Form !== 결재선 정보 form ==!
        aprvPathInfo: useForm({
            defaultSchema: SF_APRV_PRCSS_PATH_MGMT_INFO,
            defaultValues: {
                bsopClsfCd: "OSC",
            },
        }),
        // Serach Approvers List Form !== 결재자 검색 폼 ==!
        aprvPathSrchInfo: useForm({
            defaultSchema: SF_APRV_PRCSS_PATH_MGMT_INFO,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        // Approval Path Management List !== 결재선 검색 결과 결재자 목록 ==!
        aprvPathSrchList: useGrid({
            defaultSchema: SG_APRV_PATH_SRCH_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        // Approval Path Management List !== 결재선 검색 결과 결재자 목록 ==!
        aprvPathSrchAddList: useGrid({
            defaultSchema: SG_APRV_PATH_SRCH_ADD_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        //Check Approval Path Management List !== 결재선 관리 목록 조회==!
        getAprvPathSrchList: useFetch({
            api: (page = grid.aprvPathSrchList.page) =>
                APIS.getAprvPathSrchList(form.aprvPathSrchInfo.getValues(), page, grid.aprvPathSrchList.size),
            enabled: comnUtils.isEmpty(form.aprvPathSrchInfo.errors),
            key: [grid.aprvPathSrchList.page, grid.aprvPathSrchList.size],
            onSuccess: (rtnData) => {
                console.log("rtnData", rtnData);
                setAprvPathSrchList(comnUtils.getGridData(rtnData?.aprvPathSrchList.content));
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),
        // Create Organization Information !== 결재선 정보 등록 ==!
        saveAprvPathInfo: useFetch({
            api: (data: any) => APIS.createAprvPathInfo(data),
            enabled: false,
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00069",
                    onConfirm: () => navigate(`${URLS.osci0108001q}?menuId=${menuId}`),
                    close: false,
                }); // Successfully inserted. !== 정상적으로 등록되었습니다. ==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.osc.00045" }), // Failed to save. !== 저장이 실패하였습니다. ==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Click Approval Path Save Button !== 결재선 저장 버튼 클릭 ==!
        onCreateBtnClick: () => {
            const setList: any[] = grid.aprvPathSrchAddList.getData() || [];
            if (!comnUtils.isEmpty(form.aprvPathInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            if (comnUtils.isEmpty(setList)) {
                modal.openModal({ content: "msg.osc.00009" }); // No data has been selected. 선택된 데이터가 없습니다.
                return;
            }
            // save Approval Path Information !== 결재선 정보 저장 ==!
            form.aprvPathInfo.handleSubmit(
                () => {
                    const usrDmnIds: string[] = [];
                    setList.forEach((item) => {
                        // usrDmnIds.push(`${item.usrDmnId}`);
                        usrDmnIds.push(`${item.usrDmnId}`);
                    });
                    form.aprvPathInfo.setValue("usrDmnId", usrDmnIds.join(","));
                    const data = form.aprvPathInfo.getValues();
                    modal.openModal({
                        content: "msg.osc.00090", // Do you want to save ? !== 저장 하시겠습니까 ? ==!
                        onConfirm: () => {
                            fetch.saveAprvPathInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Click Search Button !== 검색버튼 ==!
        onSrchBtnClick: () => {
            fetch.getAprvPathSrchList.fetch();
        },
        // Click List Button !== 목록 버튼 클릭 ==!
        onListBtnClick: () => {
            navigate(`${URLS.osci0108001q}?menuId=${menuId}`);
        },
        // Add Grid Row Button !== 그리드 행 추가 버튼 ==!
        onGridBtnAdd: () => {
            const trgtUsrList = grid.aprvPathSrchList.getChecked();

            if (trgtUsrList.length === 0) {
                modal.openModal({ content: "msg.osc.00197" }); // Please select a registration target !== 등록대상을 선택하세요. ==!
                return;
            }

            trgtUsrList.forEach((item: any) => grid.aprvPathSrchAddList.addRow({ ...item }));
            grid.aprvPathSrchList.deleteRow("checkbox");
        },
        // Delete Grid Row !== 그리드 행 삭제 버튼 ==!
        onGridBtnDel: () => {
            const usrList = grid.aprvPathSrchAddList.getChecked();

            if (usrList.length === 0) {
                modal.openModal({ content: "msg.osc.00193" }); // 삭제대상을 선택하세요.
                return;
            }
            modal.openModal({
                content: "msg.osc.00062",
                onConfirm: () => {
                    grid.aprvPathSrchAddList.deleteRow("checkbox");
                    usrList.forEach((item: any) => grid.aprvPathSrchList.addRow({ ...item }));
                },
            });
        },
        // Grid UpRow Button  !== 버튼 클릭시 그리드 위 이동 ==!
        onGridUpRow: () => {
            const trgtUsrList = grid.aprvPathSrchAddList.getChecked();
            if (trgtUsrList.length === 0) {
                modal.openModal({ content: "msg.osc.00197" }); // 등록대상을 선택하세요.
                return;
            }
            trgtUsrList.forEach((item: any) => grid.aprvPathSrchAddList.upRow({ ...item }));
        },
        // Grid DownRow Button !== 버튼 클릭시 그리드 아래 이동 ==!
        onGridDownRow: () => {
            const trgtUsrList = grid.aprvPathSrchAddList.getChecked();
            if (trgtUsrList.length === 0) {
                modal.openModal({ content: "msg.osc.00197" }); // 등록대상을 선택하세요.
                return;
            }
            trgtUsrList.forEach((item: any) => grid.aprvPathSrchAddList.downRow({ ...item }));
        },
        // Remove previous selected user !== 기존에 선택된 사용자 대상 목록에서 제거==!
        removePrevSelect: () => {
            let deleteArr: any[] = [];
            grid.aprvPathSrchAddList.getData().forEach((item: any) => {
                let usrDmnId = item.usrDmnId;
                console.log("usrIdntfNo", usrDmnId);
                grid.aprvPathSrchList.getData().forEach((item: any) => {
                    if (item.usrDmnId === usrDmnId) {
                        console.log("selected item", item);
                        deleteArr.push(item);
                    }
                });
            });
            if (deleteArr.length > 0) {
                for (let i = 0; i < deleteArr.length; i++) {
                    console.log("delete item", deleteArr[i]);
                    grid.aprvPathSrchList.deleteRow(deleteArr[i]);
                }
            }
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {}, []);

    useEffect(() => {
        // 대상자 정보가 로딩되면 기존에 선택된 사용자를 제거해 줌
        handler.removePrevSelect();
    }, [aprvPathSrchList]);

    return (
        <Page>
            <Layout>
                <Group>
                    <Group.Body>
                        <Group.Title title={t("T_APRV_PATH_INFO")} />
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.aprvPathInfo.schema.bsopClsfCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.aprvPathInfo.schema.aprvPrcssPathNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.aprvPathInfo.schema.aprvPrcssPathDesc}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </Layout>

            <Layout>
                <Group>
                    <Group.Body>
                        <Group.Title title={t("T_APVR_SRCH")} />
                        <Group.Section>
                            <Group.Row>
                                {/* <Group.Control {...form.aprvPathSrchInfo.schema.cstmCd}></Group.Control> */}
                                <Group.Control {...form.aprvPathSrchInfo.schema.usrDmnId}></Group.Control>
                                <Group.Control {...form.aprvPathSrchInfo.schema.usrNm}></Group.Control>
                            </Group.Row>
                            {/* <Group.Row> */}
                            {/* <Group.Control {...form.aprvPathSrchInfo.schema.cstmOfcrCd}></Group.Control> */}
                            {/* </Group.Row> */}
                        </Group.Section>
                        <Layout direction="row">
                            <Layout.Left>
                                <Layout.Button role={"reset"} onClick={() => form.aprvPathSrchInfo.reset()} />
                            </Layout.Left>
                            <Layout.Right>
                                <Button role={"search"} onClick={handler.onSrchBtnClick} />
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </Layout>

            <Layout direction={"row"}>
                <Layout.Right>
                    <IconButton icon="up" size="lg" onClick={handler.onGridUpRow} />
                    <IconButton icon="down" size="lg" onClick={handler.onGridDownRow} />
                </Layout.Right>
            </Layout>

            <Layout direction={"row"}>
                <Layout>
                    <Group>
                        <Group.Body>
                            <Group.Title title={t("T_USR_LST")} />
                            <Group.Section>
                                <Grid {...grid.aprvPathSrchList.grid} data={aprvPathSrchList} />
                            </Group.Section>
                        </Group.Body>
                    </Group>
                    <IconButton icon="right" size="lg" onClick={handler.onGridBtnAdd} />
                    <IconButton icon="left" size="lg" onClick={handler.onGridBtnDel} />
                    <Group>
                        <Group.Body>
                            <Group.Title title={t("T_APRV_LIST")} />
                            <Group.Section>
                                <Grid {...grid.aprvPathSrchAddList.grid} />
                            </Group.Section>
                        </Group.Body>
                    </Group>
                </Layout>
            </Layout>
            <Layout>
                <Layout.Left>
                    <Layout.Button role={"list"} onClick={handler.onListBtnClick} />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button role={"save"} onClick={handler.onCreateBtnClick} />
                </Layout.Right>
            </Layout>
        </Page>
    );
};
